import React, { useState, useEffect, memo } from 'react';
import { MomentData, LinkedMoment, Milestone } from '../types';
import { getMilestoneByName } from './utils';
import { getMilestoneCellProps } from './useMilestoneCell';
import { MilestoneIcons } from '../../../components/Icons/MilestoneIcons';
import MilestoneTooltip from './MilestoneTooltip';
import PhaseDateCell from './PhaseDateCell';
import styles from './MilestoneRow.module.scss';

interface MilestoneRowProps {
  moment: MomentData | LinkedMoment;
  isLinked?: boolean;
  columnsConfig: any[];
  hoveredRow: string | null;
  rowHoveredClass: string;
  onRowHover: (issueId: string) => void;
  onRowLeave: () => void;
}

const MilestoneRow: React.FC<MilestoneRowProps> = memo(
  ({
    moment,
    isLinked = false,
    columnsConfig,
    hoveredRow,
    rowHoveredClass,
    onRowHover,
    onRowLeave
  }) => {
    const isHovered = hoveredRow === moment.issueId;
    const [hoveredMilestone, setHoveredMilestone] = useState<Milestone | null>(
      null
    );
    const [cursorPos, setCursorPos] = useState({
      x: 0,
      y: 0,
      cellRight: 0,
      cellLeft: 0
    });

    // Hide tooltip on scroll
    useEffect(() => {
      const handleScroll = () => {
        if (hoveredMilestone) {
          setHoveredMilestone(null);
        }
      };

      window.addEventListener('scroll', handleScroll, true);
      return () => {
        window.removeEventListener('scroll', handleScroll, true);
      };
    }, [hoveredMilestone]);

    const handleMilestoneHover = (
      e: React.MouseEvent<HTMLTableCellElement>,
      milestone: Milestone | null
    ) => {
      if (milestone) {
        const rect = e.currentTarget.getBoundingClientRect();
        const midX = rect.left + rect.width / 2;
        const midY = rect.top + rect.height / 2;
        setCursorPos({
          x: midX,
          y: midY + 10,
          cellRight: rect.right,
          cellLeft: rect.left
        });
        setHoveredMilestone(milestone);
      }
    };

    return (
      <tr
        className={`${isHovered ? rowHoveredClass : ''} ${
          isLinked ? 'linked-row' : ''
        }`}
        onMouseEnter={() => onRowHover(moment.issueId)}
        onMouseLeave={onRowLeave}
      >
        {columnsConfig.map(column => {
          // Handle phase date columns
          if ((column as any).phaseDate) {
            return (
              <PhaseDateCell key={column.key} moment={moment} column={column} />
            );
          }

          // Handle regular milestone columns
          const milestone = getMilestoneByName(
            moment.milestones,
            column.milestoneName
          );
          const {
            isTBD,
            displayText,
            bgStyle,
            dateTextStyle,
            iconName,
            hasRecordingLink,
            recordingLink,
            hasDocumentLink,
            documentLink
          } = getMilestoneCellProps(milestone);

          return (
            <td
              key={column.key}
              style={{ width: column.width, ...bgStyle }}
              className={styles.milestoneCell}
              onMouseEnter={e => handleMilestoneHover(e, milestone)}
              onMouseLeave={() => setHoveredMilestone(null)}
            >
              {(displayText || isTBD) && (
                <div className={styles.milestoneContent}>
                  {isTBD ? (
                    <div className={`${styles.dateText} ${styles.tbdText}`}>
                      TBD
                    </div>
                  ) : (
                    <div className={styles.milestoneValueContent}>
                      {/* Icons container */}
                      <div className={styles.iconsContainer}>
                        {/* Primary status icon */}
                        {iconName && MilestoneIcons[iconName] && (
                          <span className={styles.statusIcon}>
                            {(() => {
                              const IconComponent = MilestoneIcons[iconName];
                              return <IconComponent size={12} />;
                            })()}
                          </span>
                        )}

                        {/* Recording/Video icon */}
                        {hasRecordingLink && (
                          <span
                            className={`${styles.statusIcon} ${styles.clickableIcon}`}
                            onClick={e => {
                              e.stopPropagation();
                              window.open(
                                recordingLink!,
                                '_blank',
                                'noopener,noreferrer'
                              );
                            }}
                          >
                            {(() => {
                              const VideoIcon = MilestoneIcons.video;
                              return <VideoIcon size={12} />;
                            })()}
                          </span>
                        )}
                      </div>

                      {/* Milestone text - clickable if has document link */}
                      {hasDocumentLink ? (
                        <span
                          className={`${styles.dateText} ${styles.clickableText}`}
                          style={dateTextStyle}
                          onClick={e => {
                            e.stopPropagation();
                            window.open(
                              documentLink!,
                              '_blank',
                              'noopener,noreferrer'
                            );
                          }}
                        >
                          {displayText}
                        </span>
                      ) : (
                        <span className={styles.dateText} style={dateTextStyle}>
                          {displayText}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}
            </td>
          );
        })}

        {/* Tooltip - simple, no calculations */}
        {hoveredMilestone && (
          <MilestoneTooltip
            milestone={hoveredMilestone}
            cursorPos={cursorPos}
          />
        )}
      </tr>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison for better performance
    return (
      prevProps.moment.issueId === nextProps.moment.issueId &&
      prevProps.moment.milestoneId === nextProps.moment.milestoneId &&
      prevProps.hoveredRow === nextProps.hoveredRow &&
      prevProps.isLinked === nextProps.isLinked
    );
  }
);

MilestoneRow.displayName = 'MilestoneRow';

export default MilestoneRow;
